import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, FileText, Layers, AlertTriangle, TrendingUp, CheckCircle, XCircle, Clock, MousePointerClick, Eye, Target, BarChart2, X, Globe } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useOrgId } from "../hooks/useOrgId";
import SetupChecklist from "../components/SetupChecklist";
import OverviewTrends from "../components/OverviewTrends";
import { useAutoSyncRefresh } from "../hooks/useAutoSyncRefresh";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";
const PRIMARY_LIGHT = "#E6F5EE";

const CLUSTER_COLORS = [
  "#018D4C", "#FF2A00", "#FF8C00", "#9B59B6", "#1ABC9C",
  "#E67E22", "#3498DB", "#E74C3C", "#2ECC71", "#8E44AD",
  "#16A085", "#D35400", "#2980B9", "#27AE60", "#0055FF",
];

function StatCard({ label, value, sub, icon: Icon, color = PRIMARY, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">{label}</p>
          <p className="text-4xl font-bold text-zinc-900">{value}</p>
          {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
        <div className="p-2.5 mt-1 rounded-lg" style={{ background: color + "15" }}>
          <Icon size={20} className="stroke-[2.5]" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function GscPanel({ gsc }) {
  if (!gsc) return null;
  const { site_total, pages_matched, articles_total, refreshed_at, stale } = gsc;
  const stats = [
    { label: "Clicks (28d)", value: site_total?.clicks ?? 0, icon: MousePointerClick, color: "#018D4C" },
    { label: "Impressions (28d)", value: (site_total?.impressions ?? 0).toLocaleString(), icon: Eye, color: "#3498DB" },
    { label: "Avg CTR", value: `${((site_total?.ctr ?? 0) * 100).toFixed(1)}%`, icon: Target, color: "#9B59B6" },
    { label: "Avg Position", value: (site_total?.position ?? 0).toFixed(1), icon: BarChart2, color: "#FF8C00" },
  ];
  return (
    <div data-testid="gsc-overview-panel" className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Search Performance</p>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-zinc-400">
            {pages_matched}/{articles_total} pages matched
          </span>
          {refreshed_at && (
            <span className="text-[10px] text-zinc-400">
              Updated {new Date(refreshed_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
      {stale && (
        <div
          data-testid="gsc-stale-warning"
          className="flex items-center gap-2 mb-3 px-3 py-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-700"
        >
          <AlertTriangle size={14} className="stroke-[2.5] shrink-0" />
          Content synced after this GSC refresh — numbers below are stale. Click "Refresh GSC Data" on the Settings page to update.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">{label}</p>
                <p className="text-3xl font-bold text-zinc-900 tabular-nums">{value}</p>
              </div>
              <div className="p-2.5 mt-1 rounded-lg" style={{ background: color + "15" }}>
                <Icon size={18} className="stroke-[2.5]" style={{ color }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SyncPanel({ syncStatus, onSync, onFullSync, onRecluster, onRescore, onReset }) {
  const isRunning = syncStatus?.status === "running";
  const isCompleted = syncStatus?.status === "completed";
  const isFailed = syncStatus?.status === "failed";
  const hasEmbeddings = (syncStatus?.embeddings_generated ?? 0) > 0 ||
    (syncStatus?.articles_synced ?? 0) > 0;
  const noClusters = (syncStatus?.clusters_created ?? 0) === 0;
  const showRecluster = !isRunning && hasEmbeddings && (isFailed || (isCompleted && noClusters));
  const showRescore = !isRunning && isCompleted && !noClusters;

  return (
    <div className="bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">CMS Sync</p>
          <p className="text-sm text-zinc-500">{syncStatus?.message || "No sync yet"}</p>
        </div>
        <div className="flex items-center gap-2">
          {showRescore && (
            <button
              data-testid="rescore-button"
              onClick={onRescore}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wide border rounded-lg transition-all hover:opacity-90"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              <RefreshCw size={12} strokeWidth={2.5} />
              RECALCULATE SCORES
            </button>
          )}
          {showRecluster && (
            <button
              data-testid="recluster-button"
              onClick={onRecluster}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wide border rounded-lg transition-all hover:opacity-90"
              style={{ borderColor: PRIMARY, color: PRIMARY }}
            >
              <RefreshCw size={12} strokeWidth={2.5} />
              RETRY CLUSTERING
            </button>
          )}
          {isRunning && (
            <button
              data-testid="reset-sync-button"
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wide border rounded-lg transition-all hover:opacity-90"
              style={{ borderColor: "#FF2A00", color: "#FF2A00" }}
            >
              <X size={12} strokeWidth={2.5} />
              RESET
            </button>
          )}
          <button
            data-testid="sync-button"
            onClick={onSync}
            disabled={isRunning}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold tracking-wide transition-all rounded-lg ${
              isRunning
                ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                : "text-white hover:opacity-90"
            }`}
            style={{ backgroundColor: isRunning ? "" : PRIMARY }}
          >
            <RefreshCw size={14} strokeWidth={2} className={isRunning ? "animate-spin" : ""} />
            {isRunning ? "SYNCING..." : "SYNC NOW"}
          </button>
          {!isRunning && (
            <button
              data-testid="full-sync-button"
              onClick={onFullSync}
              title="Re-fetch all CMS content, ignoring incremental cache"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wide border rounded-lg transition-all hover:opacity-90"
              style={{ borderColor: "#3498DB", color: "#3498DB" }}
            >
              FULL SYNC
            </button>
          )}
        </div>
      </div>

      {(isRunning || isCompleted || isFailed) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-zinc-500">{syncStatus.progress || 0}%</span>
            <div className="flex items-center gap-1.5">
              {isCompleted && <CheckCircle size={14} className="text-green-600" strokeWidth={2} />}
              {isFailed && <XCircle size={14} className="text-red-500" strokeWidth={2} />}
              {isRunning && <Clock size={14} className="text-zinc-900" strokeWidth={2} />}
              <span className={`text-xs font-bold ${isCompleted ? "text-green-600" : isFailed ? "text-red-500" : "text-zinc-900"}`}>
                {syncStatus.status?.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              data-testid="sync-progress-bar"
              className={`h-full transition-all duration-500 rounded-full ${isRunning ? "animate-pulse" : ""} ${
                isFailed ? "bg-red-500" : ""
              }`}
              style={{ width: `${syncStatus.progress || 0}%`, backgroundColor: isFailed ? "" : PRIMARY }}
            />
          </div>
          {(syncStatus.articles_synced > 0 || syncStatus.commercial_pages_synced != null) && (
            <div className={`grid gap-3 mt-3 ${syncStatus.commercial_pages_synced != null ? "grid-cols-2" : "grid-cols-3"}`}>
              {[
                { label: "Articles", val: syncStatus.articles_synced ?? 0 },
                { label: "Embedded", val: syncStatus.embeddings_generated ?? 0 },
                { label: "Clusters", val: syncStatus.clusters_created ?? 0 },
                // Commercial pages go to site_pages, never into articles/clusters.
                ...(syncStatus.commercial_pages_synced != null
                  ? [{ label: "Commercial", val: syncStatus.commercial_pages_synced }] : []),
              ].map(({ label, val }) => (
                <div key={label} className="bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2 text-center">
                  <p className="text-lg font-bold text-zinc-900">{val}</p>
                  <p className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ROLE_LABELS = { articles: "Articles", commercial: "Commercial pages" };

// Webflow collections with their live item count next to what's synced here.
function CmsSourcePanel({ source, synced }) {
  if (!source) return null;
  const { siteName, collections, counts } = source;
  return (
    <div data-testid="cms-source-panel" className="bg-white rounded-xl border border-zinc-100 p-5 mt-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Webflow Source</p>
        {siteName && <span className="text-[10px] text-zinc-400">{siteName}</span>}
      </div>
      <div className="space-y-2">
        {collections.map(c => (
          <div key={c.id} className="flex justify-between items-center py-2 border-b border-zinc-50 last:border-0">
            <div className="min-w-0">
              <p className="text-xs font-bold text-zinc-900 truncate">{c.name}</p>
              <p className="text-[10px] text-zinc-400">{ROLE_LABELS[c.role]} · /{c.slug}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-zinc-900 tabular-nums">{counts?.[c.id] ?? "—"}</p>
              <p className="text-[10px] text-zinc-400">
                {counts ? "in Webflow" : "loading"}{synced[c.role] != null ? ` · ${synced[c.role]} synced` : ""}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const orgId = useOrgId();
  const [data, setData] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ status: "idle" });
  const [loading, setLoading] = useState(true);
  const [cmsSource, setCmsSource] = useState(null);

  // Live counts cost ~2 Webflow calls per collection, so load once per visit, off the critical path.
  useEffect(() => {
    let cancelled = false;
    setCmsSource(null);
    (async () => {
      try {
        const conn = await api.getCmsConnection();
        const collections = (conn?.config?.collections || []).filter(c => c.role !== "ignore");
        if (conn?.cms_type !== "webflow" || !conn.has_secret || !collections.length) return;
        const base = { siteName: conn.config.site_name, collections, counts: null };
        if (!cancelled) setCmsSource(base);
        const res = await api.listWebflowCollections();
        if (!res.success) {
          console.warn("[overview] Webflow collection counts unavailable:", res.message);
          return;
        }
        const counts = Object.fromEntries(res.collections.map(c => [c.id, c.item_count]));
        console.log("[overview] Webflow collection counts:", counts);
        if (!cancelled) setCmsSource({ ...base, counts });
      } catch (e) {
        console.error("[overview] CMS source load failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const loadData = useCallback(async () => {
    try {
      console.log("[loadData] fetching overview...");
      const overview = await api.getOverview();
      console.log("[loadData] response:", {
        published: overview.published_articles,
        drafts: overview.draft_articles,
        clusters: overview.total_clusters,
        cannibalization: overview.cannibalization_count,
        sync_status: overview.sync_status,
      });
      setData(overview);
      setSyncStatus(overview.sync_status || { status: "idle" });
      console.log("[loadData] state updated");
    } catch (e) {
      console.error("[loadData] failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, orgId]);

  // Refresh when a background auto-sync completes
  useAutoSyncRefresh(loadData);

  // Poll sync status
  const stuckRef = useRef({ progress: null, ticksUnchanged: 0 });
  const commercialRefreshedRef = useRef(false);
  useEffect(() => {
    if (syncStatus?.status !== "running") return;
    stuckRef.current = { progress: null, ticksUnchanged: 0 };
    commercialRefreshedRef.current = false;
    const iv = setInterval(async () => {
      try {
        const s = await api.getSyncStatus();
        console.log(
          `[sync:poll] status=${s.status} progress=${s.progress}% msg="${s.message}" ` +
          `articles=${s.articles_synced ?? 0} embedded=${s.embeddings_generated ?? 0} clusters=${s.clusters_created ?? 0}`
        );

        // progress=62 "Tagging primary topics..." is where a stalled Ollama
        // call can hang forever (see topic_tagging_service._TAG_TIMEOUT_SECONDS)
        // — flag it here since the backend gives no other signal a poll can see.
        if (s.progress === stuckRef.current.progress) {
          stuckRef.current.ticksUnchanged += 1;
          if (stuckRef.current.ticksUnchanged === 15) { // ~30s unchanged
            console.warn(
              `[sync:poll] STUCK — progress has been ${s.progress}% ("${s.message}") for ~30s. ` +
              `If this is progress=62 ("Tagging primary topics..."), Ollama is likely stalled — ` +
              `use the Reset button and retry.`
            );
          }
        } else {
          stuckRef.current = { progress: s.progress, ticksUnchanged: 0 };
        }

        if (s.status === "failed") {
          console.error(`[sync:poll] Sync failed: ${s.message}`);
        }
        // Commercial pages are upserted to site_pages at ~15%, long before the
        // sync ends — refetch once so the Commercial Pages card doesn't lag.
        if (s.commercial_pages_synced != null && !commercialRefreshedRef.current) {
          commercialRefreshedRef.current = true;
          loadData();
        }
        setSyncStatus(s);
        if (s.status !== "running") {
          console.log(`[sync:poll] Sync ended status=${s.status} — calling loadData`);
          loadData();
        }
      } catch (e) {
        console.error("[sync:poll] getSyncStatus failed:", e);
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [syncStatus?.status, loadData]);

  const handleSync = async () => {
    try {
      console.log("[sync] Starting full sync");
      await api.startSync();
      setSyncStatus({ status: "running", progress: 0, message: "Starting sync..." });
    } catch (e) {
      console.error("[sync] startSync failed:", e);
    }
  };

  const handleRecluster = async () => {
    try {
      console.log("[sync] Starting recluster");
      await api.startRecluster();
      setSyncStatus(s => ({ ...s, status: "running", progress: 0, message: "Starting clustering..." }));
    } catch (e) {
      console.error("[sync] startRecluster failed:", e);
    }
  };

  const handleRescore = async () => {
    try {
      console.log("[sync] Starting rescore");
      await api.startRescore();
      setSyncStatus(s => ({ ...s, status: "running", progress: 0, message: "Recalculating scores..." }));
    } catch (e) {
      console.error("[sync] startRescore failed:", e);
    }
  };

  const handleRelabel = async () => {
    try {
      console.log("[sync] Starting relabel");
      await api.startRelabel();
      setSyncStatus(s => ({ ...s, status: "running", progress: 0, message: "Relabeling topics..." }));
    } catch (e) {
      console.error("[sync] startRelabel failed:", e);
    }
  };

  const handleReset = async () => {
    try {
      console.log("[sync] Resetting sync state");
      await api.resetSync();
      setSyncStatus({ status: "idle" });
    } catch (e) {
      console.error("[sync] resetSync failed:", e);
    }
  };

  const handleFullSync = async () => {
    try {
      console.log("[sync] Starting full sync (force_full=true)");
      await api.startFullSync();
      setSyncStatus({ status: "running", progress: 0, message: "Starting full sync..." });
    } catch (e) {
      console.error("[sync] startFullSync failed:", e);
    }
  };

  const chartData = (data?.clusters || []).map((c, i) => ({
    name: c.label?.length > 16 ? c.label.slice(0, 16) + "…" : c.label || `Topic ${i + 1}`,
    articles: c.size,
    color: CLUSTER_COLORS[i % CLUSTER_COLORS.length],
  }));
  const hasUnlabeledClusters = (data?.clusters || []).some(c => /^Topic \d+$/.test(c.label || ""));

  return (
    <div data-testid="overview-page" className="p-6 sm:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 pb-6 border-b border-zinc-100">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Dashboard</p>
        <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">
          Content Coverage Intelligence
        </h1>
      </div>

      <SetupChecklist
        onSync={handleSync}
        syncing={syncStatus?.status === "running"}
        refreshKey={`${orgId}:${data?.total_articles ?? 0}:${syncStatus?.status}`}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <StatCard label="Published" value={loading ? "—" : data?.published_articles ?? 0} sub="articles" icon={FileText} testid="stat-published" />
        <StatCard label="Drafts" value={loading ? "—" : data?.draft_articles ?? 0} sub="articles" icon={FileText} color="#FF8C00" testid="stat-drafts" />
        <StatCard label="Topic Clusters" value={loading ? "—" : data?.total_clusters ?? 0} icon={Layers} color={PRIMARY} testid="stat-clusters" />
        <StatCard label="Cannibalization" value={loading ? "—" : data?.cannibalization_count ?? 0} icon={AlertTriangle} color="#FF2A00" testid="stat-cannibalization" />
        <StatCard
          label="Coverage Gap"
          value={loading ? "—" : (data?.total_clusters ? Math.max(0, (data?.total_clusters || 0) - Math.ceil((data?.total_articles || 0) / 5)) : 0)}
          icon={TrendingUp}
          color="#FF8C00"
          testid="stat-gap"
        />
        <StatCard
          label="Commercial Pages"
          value={loading ? "—" : data?.commercial_pages ?? 0}
          sub={!loading && data?.commercial_page_gaps > 0 ? `${data.commercial_page_gaps} no blog links` : undefined}
          icon={Globe}
          color="#1D4ED8"
          testid="stat-commercial-pages"
        />
      </div>

      {/* GSC Search Performance */}
      <GscPanel gsc={data?.gsc} />

      {/* Month-over-month: publishing pace, cannibalization, topic distribution */}
      <OverviewTrends trends={data?.trends} />

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Sync panel */}
        <div className="lg:col-span-1">
          <SyncPanel syncStatus={syncStatus} onSync={handleSync} onFullSync={handleFullSync} onRecluster={handleRecluster} onRescore={handleRescore} onReset={handleReset} />

          <CmsSourcePanel
            source={cmsSource}
            synced={loading ? {} : { articles: data?.total_articles ?? 0, commercial: data?.commercial_pages ?? 0 }}
          />

          {/* Quick info */}
          {data?.largest_cluster && (
            <div className="bg-white rounded-xl border border-zinc-100 p-5 mt-4 shadow-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Cluster Insights</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-zinc-50">
                  <span className="text-xs text-zinc-500">Largest Topic</span>
                  <span className="text-xs font-bold text-zinc-900">{data.largest_cluster.label} ({data.largest_cluster.size})</span>
                </div>
                {data?.smallest_cluster && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-xs text-zinc-500">Smallest Topic</span>
                    <span className="text-xs font-bold text-zinc-900">{data.smallest_cluster.label} ({data.smallest_cluster.size})</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cluster chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Topic Coverage Distribution</p>
              {hasUnlabeledClusters && (
                <button
                  data-testid="relabel-topics-button"
                  onClick={handleRelabel}
                  disabled={syncStatus?.status === "running"}
                  title="Some topics still show placeholder names (Topic N) — click to relabel now instead of waiting for the automatic post-sync pass"
                  className="text-zinc-400 hover:text-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw size={12} strokeWidth={2.5} className={syncStatus?.status === "running" ? "animate-spin" : ""} />
                </button>
              )}
            </div>
            {chartData.length > 0 && (
              <button onClick={() => navigate("/clusters")} className="text-[10px] tracking-widest uppercase font-bold transition-colors hover:underline" style={{ color: PRIMARY }}>
                View All →
              </button>
            )}
          </div>
          {chartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <RefreshCw size={32} className="text-zinc-300" />
              <p className="text-sm text-zinc-500 mt-3">No data yet.</p>
              <p className="text-xs text-zinc-400 mt-1">Click "Sync Now" to fetch your CMS articles.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barSize={16}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  cursor={{ fill: "#F4F4F5" }}
                />
                <Bar dataKey="articles" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  );
}
